import {ActionList, ActionMenu, Label} from '@primer/react'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {isAllCondition} from '../../helpers/conditions'
import {INCLUDE_ALL_PATTERN} from '@github-ui/repos-rules-types/rules-types'
import type {
  Condition,
  ConditionParameters,
  ExpandedTargetType,
  ExpandedOrgTargetType,
  SourceType,
} from '@github-ui/repos-rules-types/rules-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'

const ORG_TARGET_OPTIONS: Option[] = [
  {
    condition: 'all_orgs',
    name: 'All organizations',
    description: 'Target all organizations within the enterprise',
    beta: false,
  },
  {
    condition: 'organization_name',
    name: 'Dynamic list by name',
    description: 'Target organizations based on name',
    beta: false,
  },
  {
    condition: 'organization_property',
    name: 'Dynamic list by property',
    description: 'Target organizations based on properties',
    beta: false,
  },
  {
    condition: 'organization_id',
    name: 'Select organizations',
    description: `Target a specific list of selected organizations`,
    beta: false,
  },
]

export function OrganizationTargetTypeSelector({
  sourceType,
  currentOrgCondition,
  setOrgCondition,
}: {
  sourceType: SourceType
  currentOrgCondition: Condition
  setOrgCondition: (val: ExpandedOrgTargetType) => void
}) {
  const selectedTargetType = isAllCondition(currentOrgCondition.target, currentOrgCondition.parameters)
    ? 'all_orgs'
    : currentOrgCondition.target

  const excludeConditions = sourceType === 'enterprise' ? [] : ['organization_property']
  const options = ORG_TARGET_OPTIONS.filter(({condition}) => !excludeConditions.includes(condition))

  return (
    <TargetTypeSelector
      selectedTargetType={selectedTargetType}
      selectOption={(val: Option) => setOrgCondition(val.condition as ExpandedOrgTargetType)}
      options={options}
    />
  )
}

type Option = {
  condition: ExpandedTargetType
  name: string
  description: string
  beta: boolean
}

function TargetTypeSelector({
  selectedTargetType,
  selectOption,
  options,
}: {
  selectedTargetType: ExpandedTargetType
  selectOption: (val: Option) => void
  options: Option[]
}) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const selectedOption = options.find(o => o.condition === selectedTargetType) || options[0]!
  const enabled = isFeatureEnabled('lifecycle_label_name_updates')

  return (
    <ActionMenu>
      <ActionMenu.Button>
        <div className="d-flex gap-1">
          <span className="text-normal">Target:</span>
          <span className="text-bold">{selectedOption.name}</span>
        </div>
      </ActionMenu.Button>
      <ActionMenu.Overlay width="medium">
        <ActionList selectionVariant="single">
          {options.map(option => (
            <ActionList.Item
              selected={option === selectedOption}
              key={option.name}
              onSelect={() => option !== selectedOption && selectOption(option)}
            >
              {option.name}
              <ActionList.Description variant="block">{option.description}</ActionList.Description>
              {option.beta && (
                <ActionList.TrailingVisual>
                  {enabled ? <BetaLabel /> : <Label variant="success">Beta</Label>}
                </ActionList.TrailingVisual>
              )}
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

export function emptyParametersByType(targetType: ExpandedTargetType): ConditionParameters {
  switch (targetType) {
    case 'repository_name':
    case 'ref_name':
    case 'repository_property':
    case 'organization_name':
    case 'organization_property':
    case 'workflow_path':
      return {include: [], exclude: []}
    case 'repository_id':
      return {repository_ids: []}
    case 'organization_id':
      return {organization_ids: []}
    case 'all_repos':
    case 'all_orgs':
      return {include: [INCLUDE_ALL_PATTERN], exclude: []}
    default:
      return {include: [], exclude: []}
  }
}
